import React, { useState } from 'react';
import { AlertCircle, FileText, Loader, Upload } from 'lucide-react';
import { buildApiUrl } from '../config/appConfig';
import { useDocumentMetadata } from '../hooks/useDocumentMetadata';

interface DiagnosisResult {
  primaryDiagnosis: string;
  confidenceLevel: number;
  additionalFindings: string[];
  recommendedActions: string;
  aiAnalysis: string;
}

const InlineAlert: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700" role="alert">
    <span className="block sm:inline">{children}</span>
  </div>
);

const XrayDiagnosis: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [diagnosisResult, setDiagnosisResult] = useState<DiagnosisResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useDocumentMetadata(
    'Diagnostic Imaging Review',
    'Upload X-rays and PDFs to receive AI-assisted diagnostic summaries in MediInsight AI.'
  );

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0];
    if (uploadedFile) {
      setSelectedFile(uploadedFile);
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);
    setDiagnosisResult(null);

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await fetch(buildApiUrl('/analyze-image'), {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Unable to analyze the uploaded file.');
      }

      const data: DiagnosisResult = await response.json();
      setDiagnosisResult(data);
    } catch (error) {
      setErrorMessage('We could not process that file right now. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-10">
      <div className="container mx-auto px-4">
        <div className="mx-auto mb-10 max-w-4xl text-center">
          <h1 className="text-4xl font-bold text-slate-900">Diagnostic Imaging Review</h1>
          <p className="mt-4 text-lg text-slate-600">
            Upload an X-ray image or supporting PDF to receive a structured AI-generated summary, confidence level, and recommended follow-up.
          </p>
        </div>

        <div className="mx-auto max-w-4xl rounded-3xl bg-white p-8 shadow-sm ring-1 ring-slate-200">
          <h2 className="text-2xl font-semibold text-slate-900">Upload Study</h2>
          <p className="mt-2 text-sm text-slate-600">
            Supported formats include images and PDFs. Files are sent to the backend for AI-assisted analysis.
          </p>

          <div className="mt-6 flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-sky-200 bg-sky-50/60 p-8 text-center">
            <Upload className="mb-3 h-12 w-12 text-sky-600" />
            <p className="font-semibold text-sky-700">Select an X-ray image or PDF</p>
            <p className="mt-2 text-sm text-slate-500">This workflow is ideal for demos of document upload and AI-powered analysis.</p>
            <input
              id="file-upload"
              type="file"
              accept="image/*,.pdf"
              onChange={handleFileUpload}
              className="hidden"
            />
            <label
              htmlFor="file-upload"
              className="mt-5 cursor-pointer rounded-full bg-sky-600 px-5 py-3 font-semibold text-white transition hover:bg-sky-700"
            >
              Choose File
            </label>
            {selectedFile && (
              <p className="mt-4 text-sm text-slate-600">{selectedFile.name}</p>
            )}
          </div>

          <button
            onClick={handleSubmit}
            disabled={!selectedFile || isLoading}
            className={`mt-6 w-full rounded-full px-6 py-3 font-semibold text-white transition ${
              !selectedFile || isLoading
                ? 'cursor-not-allowed bg-slate-400'
                : 'bg-sky-600 hover:bg-sky-700'
            }`}
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <Loader className="mr-3 h-5 w-5 animate-spin text-white" />
                Analyzing Study...
              </span>
            ) : (
              'Run Imaging Analysis'
            )}
          </button>
        </div>

        <div className="mx-auto mt-8 max-w-4xl">
          {errorMessage && (
            <InlineAlert>
              <AlertCircle className="mr-2 inline h-4 w-4" />
              {errorMessage}
            </InlineAlert>
          )}

          {diagnosisResult && (
            <div className="rounded-3xl bg-white p-8 shadow-sm ring-1 ring-slate-200">
              <h2 className="text-2xl font-semibold text-slate-900">Analysis Summary</h2>
              <div className="mt-6 space-y-5">
                <ResultSection title="Primary Diagnosis">
                  <p className="text-slate-800">{diagnosisResult.primaryDiagnosis}</p>
                </ResultSection>

                <ResultSection title="Confidence Level">
                  <div className="mb-3 h-2.5 w-full rounded-full bg-slate-200">
                    <div
                      className="h-2.5 rounded-full bg-sky-600"
                      style={{ width: `${diagnosisResult.confidenceLevel}%` }}
                    />
                  </div>
                  <p className="text-slate-800">{diagnosisResult.confidenceLevel}% confidence</p>
                </ResultSection>

                <ResultSection title="Additional Findings">
                  <ul className="list-disc pl-5 text-slate-800">
                    {diagnosisResult.additionalFindings.map((finding) => (
                      <li key={finding}>{finding}</li>
                    ))}
                  </ul>
                </ResultSection>

                <ResultSection title="Recommended Actions">
                  <p className="text-slate-800">{diagnosisResult.recommendedActions}</p>
                </ResultSection>

                <ResultSection title="Detailed AI Analysis">
                  <div className="rounded-2xl bg-slate-50 p-4">
                    <div className="mb-2 flex items-center gap-2 text-slate-600">
                      <FileText className="h-4 w-4" />
                      <span className="text-sm font-medium">Raw assistant output</span>
                    </div>
                    <pre className="overflow-x-auto whitespace-pre-wrap text-sm text-slate-700">
                      {diagnosisResult.aiAnalysis}
                    </pre>
                  </div>
                </ResultSection>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ResultSection: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <section>
    <h3 className="text-lg font-semibold text-sky-700">{title}</h3>
    <div className="mt-2">{children}</div>
  </section>
);

export default XrayDiagnosis;
